import { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { AdminUserItem } from "@shared-core/data/adapter";
import { ApiError } from "@shared-core/data/api/errors";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { MarketingPersonResponse } from "@shared-core/data/api-types";
import { Button } from "../../primitives/button";

interface EditPersonDialogProps {
  personId: string;
  onClose: () => void;
  /** Called after a successful save so the caller can refresh its list. */
  onSaved: () => void;
}

/**
 * Edit modal for a marketing contact's own fields (name, email, phone,
 * address, account code, tags). The backend has always supported patching
 * these; this is the first frontend surface for it — the only way to fix a
 * mistake found after import (e.g. a changed email address) without
 * re-importing and risking a duplicate.
 *
 * On a contact that comes from Tharstern, a changed email becomes a CIS email
 * override the nightly Tharstern sync leaves alone; the dialog says so, shows
 * what Tharstern has, and offers to go back to it (saving the Tharstern email
 * ends the override).
 */
export function EditPersonDialog({
  personId,
  onClose,
  onSaved,
}: EditPersonDialogProps) {
  const dataAdapter = useDataAdapter();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // What Tharstern has. null when the contact has no Tharstern email (or none
  // at all: a CSV-imported or hand-created contact, whose email CIS owns).
  const [sourceEmail, setSourceEmail] = useState<string | null>(null);
  const [isEmailOverridden, setIsEmailOverridden] = useState(false);
  const [phone, setPhone] = useState("");
  const [postalAddress, setPostalAddress] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [salesRepUserId, setSalesRepUserId] = useState("");
  const [reps, setReps] = useState<AdminUserItem[]>([]);

  // Reps for the picker — every admin/manager/sales_rep/csr user, not just
  // the "sales_rep" role: contacts get assigned to managers and CSRs too
  // (e.g. the default-unassigned-rep policy target, Gary Daglish, holds
  // the sales_rep role, but Nicky Tangi — a real rep with contacts — holds
  // manager). Fetched once; independent of the person being edited.
  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .getAdminUsers({ pageSize: 200, sort: "name" })
      .then((res) => {
        if (!cancelled) setReps(res.items);
      })
      .catch((e) => {
        console.error("marketing.contacts.edit.reps_load_failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void dataAdapter
      .getMarketingPerson(personId)
      .then((p: MarketingPersonResponse) => {
        if (cancelled) return;
        setFirstName(p.first_name);
        setLastName(p.last_name);
        setEmail(p.email ?? "");
        setSourceEmail(p.source_email ?? null);
        setIsEmailOverridden(p.is_email_overridden);
        setPhone(p.phone ?? "");
        setPostalAddress(p.postal_address ?? "");
        setAccountCode(p.account_code ?? "");
        setTagsInput(p.tags.join(", "));
        setSalesRepUserId(p.sales_rep_user_id ?? "");
      })
      .catch((e) => {
        console.error("marketing.contacts.edit.load.failed", e);
        if (!cancelled) setError("Couldn't load this contact.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, personId]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await dataAdapter.updateMarketingPerson(personId, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        postal_address: postalAddress.trim() || null,
        account_code: accountCode.trim() || null,
        sales_rep_user_id: salesRepUserId || null,
        tags: tagsInput
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      });
      onSaved();
      onClose();
    } catch (e) {
      console.error("marketing.contacts.edit.save.failed", e);
      setError(
        e instanceof ApiError && e.status === 502
          ? "Couldn't sync this rep change to Tharstern. Please try again."
          : "Couldn't save changes. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-base">Edit contact</h2>
          <button onClick={onClose} aria-label="Close">
            <X className="size-5 text-muted-foreground hover:text-foreground" />
          </button>
        </div>

        {loading ? (
          <p className="px-4 py-8 text-sm text-muted-foreground text-center">
            Loading…
          </p>
        ) : (
          <div className="px-4 py-4 space-y-3">
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs text-muted-foreground">
                First name
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
                />
              </label>
              <label className="block text-xs text-muted-foreground">
                Last name
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
                />
              </label>
            </div>
            <label className="block text-xs text-muted-foreground">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby="edit-person-email-note"
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
              />
              <EmailOverrideNote
                email={email}
                sourceEmail={sourceEmail}
                isEmailOverridden={isEmailOverridden}
                onUseTharsternEmail={() => setEmail(sourceEmail ?? "")}
              />
            </label>
            <label className="block text-xs text-muted-foreground">
              Phone
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
              />
            </label>
            <label className="block text-xs text-muted-foreground">
              Postal address
              <input
                value={postalAddress}
                onChange={(e) => setPostalAddress(e.target.value)}
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
              />
            </label>
            <label className="block text-xs text-muted-foreground">
              Account code
              <input
                value={accountCode}
                onChange={(e) => setAccountCode(e.target.value)}
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
              />
            </label>
            <label className="block text-xs text-muted-foreground">
              Sales rep
              <select
                value={salesRepUserId}
                onChange={(e) => setSalesRepUserId(e.target.value)}
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
              >
                <option value="">Unassigned</option>
                {reps.map((rep) => (
                  <option key={rep.id} value={rep.id}>
                    {rep.name}
                  </option>
                ))}
              </select>
              {accountCode.trim() && (
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  This contact is linked to Tharstern account "
                  {accountCode.trim()}" — changing the rep here also updates it
                  there.
                </span>
              )}
            </label>
            <label className="block text-xs text-muted-foreground">
              Tags (comma-separated)
              <input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="supplier, wholesale"
                className="mt-1 w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-foreground"
              />
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={loading || saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The CIS-email-override note under the Email input. Nothing for a contact
 * whose email CIS owns outright (no Tharstern email, not overridden).
 */
function EmailOverrideNote({
  email,
  sourceEmail,
  isEmailOverridden,
  onUseTharsternEmail,
}: {
  email: string;
  sourceEmail: string | null;
  isEmailOverridden: boolean;
  onUseTharsternEmail: () => void;
}) {
  const fromTharstern = isEmailOverridden || sourceEmail !== null;
  if (!fromTharstern) return null;
  const typed = email.trim().toLowerCase();
  const matchesTharstern = sourceEmail !== null && typed === sourceEmail;
  if (matchesTharstern) {
    return isEmailOverridden ? (
      <span
        id="edit-person-email-note"
        className="mt-1 block text-[11px] text-muted-foreground"
      >
        Saving will go back to the Tharstern email and end the override in One.
      </span>
    ) : null;
  }
  return (
    <span
      id="edit-person-email-note"
      className="mt-1 block text-[11px] text-muted-foreground"
    >
      {isEmailOverridden
        ? "Edited in One: the nightly Tharstern sync won't change this email."
        : "Saving will override the email in One: the nightly Tharstern sync won't overwrite the email."}{" "}
      Tharstern has: {sourceEmail ?? "no email"}.{" "}
      <button
        type="button"
        onClick={onUseTharsternEmail}
        className="underline hover:text-foreground"
      >
        Use Tharstern email
      </button>
    </span>
  );
}
